import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { Task, TaskParticipant } from "@/components/atomic-crm/types";
import { TaskAssignedAvatars } from "@/components/atomic-crm/tasks/TaskAssignedAvatars";
import {
  TaskCardActionCell,
  TaskCardRow,
  TaskCardTable,
  TaskRoundCheckbox,
  TaskSortHeader,
} from "@/components/atomic-crm/tasks/TaskCardTable";
import { TaskDescriptionCell } from "@/components/atomic-crm/tasks/TaskDescriptionCell";
import { useTaskCompletionToggle } from "@/components/atomic-crm/tasks/useTaskCompletionToggle";
import { parseDateKey } from "@/modules/calendar/calendarUtils";
import { getWorkCategoryMeta } from "@/modules/work/workCategoryUtils";
import type { WorkItem } from "@/modules/work/workTypes";
import { cn } from "@/lib/utils";

const formatItemDate = (dateKey: string) =>
  parseDateKey(dateKey).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** Raw HH:MM (not the display label) so 9:00 sorts before 10:00. */
const sortKey = (item: WorkItem) =>
  `${item.date} ${("time" in item.event && item.event.time) || ""}`;

const ItemCells = ({ item }: { item: WorkItem }) => {
  const category = getWorkCategoryMeta(item.category);
  return (
    <>
      <td className="whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={cn("size-2 rounded-full", category.dotClass)} />
          {category.label}
        </span>
      </td>
      <td
        className={cn(
          "whitespace-nowrap",
          item.overdue && "font-medium text-destructive",
        )}
      >
        {item.startDate
          ? `${parseDateKey(item.startDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })} → ${formatItemDate(item.date)}`
          : formatItemDate(item.date)}
      </td>
      <td className="whitespace-nowrap text-muted-foreground">
        {item.time?.toLowerCase() ?? "—"}
      </td>
      <TaskCardActionCell className="max-w-[11rem]">
        {item.dealName && item.dealId ? (
          <Link
            to={`/deals/${item.dealId}/show`}
            className="block truncate link-action"
          >
            {item.dealName}
          </Link>
        ) : (
          <span className="text-muted-foreground">{item.dealName ?? "—"}</span>
        )}
      </TaskCardActionCell>
    </>
  );
};

const WorkTaskListRow = ({
  item,
  task,
  participants,
  onSelect,
}: {
  item: WorkItem;
  task: Task;
  participants: TaskParticipant[];
  onSelect: (item: WorkItem) => void;
}) => {
  const { toggle, checkboxChecked, checkboxDisabled, isPending } =
    useTaskCompletionToggle(task, participants);

  return (
    <TaskCardRow onClick={() => onSelect(item)} muted={item.done}>
      <TaskCardActionCell>
        <TaskRoundCheckbox
          checked={checkboxChecked}
          disabled={checkboxDisabled || isPending}
          onCheckedChange={() => toggle()}
          aria-label="Mark task complete"
        />
      </TaskCardActionCell>
      <td className="max-w-[28rem]">
        <TaskDescriptionCell
          text={task.text}
          isDone={Boolean(task.done_date)}
          enablePreview={false}
          fadeSurface="card"
        />
      </td>
      <ItemCells item={item} />
      <TaskCardActionCell>
        <TaskAssignedAvatars task={task} participants={participants} />
      </TaskCardActionCell>
    </TaskCardRow>
  );
};

const WorkEventListRow = ({
  item,
  onSelect,
}: {
  item: WorkItem;
  onSelect: (item: WorkItem) => void;
}) => (
  <TaskCardRow onClick={() => onSelect(item)} muted={item.done}>
    <td />
    <td className="max-w-[28rem]">
      <span className="line-clamp-2">{item.title}</span>
    </td>
    <ItemCells item={item} />
    <td>
      {item.assigneeInitials ? (
        <span className="inline-flex size-7 items-center justify-center rounded-full bg-muted text-2xs font-semibold">
          {item.assigneeInitials}
        </span>
      ) : null}
    </td>
  </TaskCardRow>
);

/** Calendar "List" display: same clean card table as every task list. */
export const WorkCalendarListView = ({
  items,
  participantsByTaskId,
  onSelectItem,
  emptyMessage,
}: {
  items: WorkItem[];
  participantsByTaskId: Record<string, TaskParticipant[]>;
  onSelectItem: (item: WorkItem) => void;
  emptyMessage: string;
}) => {
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) =>
        sortDirection === "asc"
          ? sortKey(left).localeCompare(sortKey(right))
          : sortKey(right).localeCompare(sortKey(left)),
      ),
    [items, sortDirection],
  );

  if (sortedItems.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  return (
    <TaskCardTable
      columns={[
        { key: "done", className: "w-10" },
        { key: "title", label: "Title" },
        { key: "type", label: "Type", className: "w-28" },
        {
          key: "due",
          label: (
            <TaskSortHeader
              label="Due date"
              direction={sortDirection}
              onToggle={() =>
                setSortDirection((current) =>
                  current === "asc" ? "desc" : "asc",
                )
              }
            />
          ),
          className: "w-40",
        },
        { key: "time", label: "Due time", className: "w-24" },
        { key: "project", label: "Project", className: "w-44" },
        { key: "assigned", label: "Assigned", className: "w-28" },
      ]}
    >
      {sortedItems.map((item) =>
        item.event.kind === "task" ? (
          <WorkTaskListRow
            key={item.id}
            item={item}
            task={item.event.task}
            participants={
              participantsByTaskId[String(item.event.task.id)] ?? []
            }
            onSelect={onSelectItem}
          />
        ) : (
          <WorkEventListRow key={item.id} item={item} onSelect={onSelectItem} />
        ),
      )}
    </TaskCardTable>
  );
};
